import React, { useEffect, useRef, useState } from "react";
import { TUTORIAL_EVENTS, TUTORIAL_TARGETS } from "./constants";
import { typography, textColors } from "../../design-system/tokens";
import AddButton from "../components/AddButton";

const ADD_BUTTON_SELECTOR = `[data-tutorial-id="${TUTORIAL_TARGETS.ADD_BUTTON}"]`;

const requestAddElementPanelOpen = () => {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(TUTORIAL_EVENTS.OPEN_ADD_ELEMENT_PANEL, {
        detail: { source: "tutorial:add_element_panel_step" },
      }),
    );
  } catch (_) {}
};

const isHiddenByStyles = (element) => {
  let current = element;

  while (current && current.nodeType === 1) {
    if (current.hasAttribute?.("hidden")) return true;
    if (current.getAttribute?.("aria-hidden") === "true") return true;

    try {
      const styles = window.getComputedStyle(current);
      if (styles.display === "none" || styles.visibility === "hidden") return true;
      if (Number(styles.opacity) === 0) return true;
    } catch (_) {
      return false;
    }

    current = current.parentElement;
  }

  return false;
};

const isAddButtonVisible = () => {
  if (typeof document === "undefined") return false;

  const buttonEl = document.querySelector(ADD_BUTTON_SELECTOR);
  if (!buttonEl) return false;
  if (!buttonEl.closest('[data-page-content="true"]')) return false;

  const rect = buttonEl.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (isHiddenByStyles(buttonEl)) return false;

  return true;
};

const AddButtonLegendRow = ({ label, description, color }) => (
  <div className="flex items-start gap-2">
    <AddButton
      small
      disabled
      zIndex={1}
      aria-hidden="true"
      tabIndex={-1}
      className="pointer-events-none opacity-95 shrink-0 !w-6 !h-6 !rounded-full"
      tutorialTargetId={null}
      backgroundColor={color}
    />
    <p className={`${typography.caption} ${textColors.secondary}`}>
      <span className="font-medium">{label}</span>: {description}
    </p>
  </div>
);

const AddButtonStepBody = () => {
  const [addButtonVisible, setAddButtonVisible] = useState(() => isAddButtonVisible());

  useEffect(() => {
    const refreshVisibility = () => {
      setAddButtonVisible(isAddButtonVisible());
    };

    refreshVisibility();
    window.addEventListener("resize", refreshVisibility);

    if (typeof MutationObserver === "undefined" || !document?.body) {
      return () => {
        window.removeEventListener("resize", refreshVisibility);
      };
    }

    const observer = new MutationObserver(() => refreshVisibility());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });

    return () => {
      window.removeEventListener("resize", refreshVisibility);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="space-y-3">
      <p>
        Click a <span className="font-medium">+</span> Add button inside the page area to open the{" "}
        <span className="font-medium">Add Element</span> panel.
      </p>

      <div className="space-y-2 rounded-lg border border-gray-200 dark:border-dark-700 p-2.5">
        <AddButtonLegendRow
          color="#16a34a"
          label="Green +"
          description="Page-level add button (creates a new section/component on the page)."
        />
        <AddButtonLegendRow
          color="#2563eb"
          label="Blue +"
          description="Component-level add button (adds content inside an existing section/component)."
        />
      </div>

      {addButtonVisible ? (
        <p className={`${typography.caption} ${textColors.muted}`}>
          A page Add button is visible now — click it to open Add Element.
        </p>
      ) : (
        <p className={`${typography.caption} ${textColors.muted}`}>
          If the button is not visible right now, hover between sections/components in the page area and it will
          appear there.
        </p>
      )}
    </div>
  );
};

const AddElementPanelStepBody = () => {
  const didRequestOpenRef = useRef(false);

  useEffect(() => {
    if (didRequestOpenRef.current) return;
    didRequestOpenRef.current = true;
    // Fallback behavior: if the user didn't click a canvas + button, request opening the panel now.
    requestAddElementPanelOpen();
  }, []);

  return (
    <div className="space-y-2">
      <p>
        This panel appears after clicking <span className="font-medium">+</span> in the page canvas.
      </p>
      <p>
        Use <span className="font-medium">Templates</span> for ready-made sections, or{" "}
        <span className="font-medium">Quick Add</span> to insert a single element fast.
      </p>
    </div>
  );
};

/**
 * @typedef {Object} EditorTutorialStep
 * @property {string} id
 * @property {string} title
 * @property {React.ReactNode} body
 * @property {string|null} targetId
 * @property {'auto'|'right'|'left'|'top'|'bottom'} [placement]
 * @property {boolean} [showNav] - If false, hide Prev/Next controls for this step (X remains visible).
 * @property {{paddingPx?: number, radiusPx?: number}} [highlight]
 * @property {string} [advanceOnEvent] - Window event name that advances this step once received.
 * @property {(evt: any) => boolean} [advanceOnEventPredicate] - Optional predicate for advanceOnEvent.
 * @property {string} [advanceOnSignal] - Named signal that advances this step via tutorial.signal().
 */

/**
 * Editor tutorial step list (copy + target ids).
 *
 * Note: DOM anchors (`data-tutorial-id`) are added in Roadmap 2.
 * This file stays editor-agnostic and is safe to edit without touching the editor UI.
 *
 * @type {EditorTutorialStep[]}
 */
export const EDITOR_TUTORIAL_STEPS = [
  {
    id: "click_any_element",
    title: "Click any element to start editing",
    targetId: TUTORIAL_TARGETS.CANVAS,
    placement: "auto",
    showNav: false, // Step 1: user must click; no Next/Prev
    highlight: { paddingPx: 12, radiusPx: 16 },
    advanceOnEvent: TUTORIAL_EVENTS.ELEMENT_CLICKED,
    body: (
      <div className="space-y-2">
        <p>Click any element on your website preview to open its editor.</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <span className="font-medium">Blue outline</span>: page-level elements (changes affect only this page).
          </li>
          <li>
            <span className="font-medium">Green outline</span>: website-level elements (navbar/sidebars/footer — changes affect the whole website).
          </li>
        </ul>
        <p className={`${typography.caption} ${textColors.muted}`}>
          Tip: hover an element to see its label, then click to select it.
        </p>
      </div>
    ),
  },
  {
    id: "edit_panel_overview",
    title: "This is the Edit Panel",
    targetId: TUTORIAL_TARGETS.EDIT_PANEL,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 10, radiusPx: 14 },
    body: (
      <div className="space-y-2">
        <p>
          The Edit Panel shows everything you can change for the currently selected element.
        </p>
        <p>
          Updates are saved automatically as you edit fields like text, colors, spacing, layout, and more.
        </p>
      </div>
    ),
  },
  {
    id: "edit_panel_tabs",
    title: "Use tabs to edit different parts",
    targetId: TUTORIAL_TARGETS.EDIT_PANEL_TABS,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          Tabs organize settings into sections (for example: <span className="font-medium">Basic</span> content, styling groups, and internal elements).
        </p>
        <p>
          If you can’t find a setting, check the other tabs — many elements have more options than the Basic fields.
        </p>
      </div>
    ),
  },
  {
    id: "add_button",
    title: "Hover this space to reveal + Add buttons",
    targetId: TUTORIAL_TARGETS.ADD_BUTTON,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    advanceOnEvent: TUTORIAL_EVENTS.ADD_BUTTON_CLICKED,
    body: <AddButtonStepBody />,
  },
  {
    id: "add_element_panel",
    title: "Add Element panel",
    targetId: TUTORIAL_TARGETS.ADD_ELEMENT_PANEL,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: <AddElementPanelStepBody />,
  },
  {
    id: "ai_assistant",
    title: "AI assistant",
    targetId: TUTORIAL_TARGETS.AI_CHAT,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          Use the AI assistant to rewrite text, improve tone, generate content, or help you structure sections.
        </p>
        <p>
          Tip: select an element first — the AI can use it as context for more precise edits.
        </p>
        <p className={`${typography.caption} ${textColors.muted}`}>
          If you don’t see the AI panel, open it from the side dock.
        </p>
      </div>
    ),
  },
  {
    id: "ai_assistant_assets",
    title: "Attach files/images and set AI profile",
    targetId: TUTORIAL_TARGETS.AI_CHAT_ASSETS,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 10, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          Attach <span className="font-medium">images</span> or <span className="font-medium">files</span> (PDF, DOC, screenshots) to give the AI extra context. Pinned assets are automatically included in your messages.
        </p>
        <p>
          Use the <span className="font-medium">Target</span> button to set your <span className="font-medium">Website profile</span> — audience, format, and writing style for AI responses.
        </p>
      </div>
    ),
  },
  {
    id: "undo_redo",
    title: "Undo / Redo",
    targetId: TUTORIAL_TARGETS.NAVBAR_UNDO_REDO,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          Use <span className="font-medium">Undo</span> and <span className="font-medium">Redo</span> to quickly revert or
          re-apply changes while editing.
        </p>
        <p className={`${typography.caption} ${textColors.muted}`}>
          Shortcuts: <span className="font-medium">Ctrl/Cmd+Z</span> to undo,{" "}
          <span className="font-medium">Ctrl/Cmd+Shift+Z</span> (or <span className="font-medium">Ctrl+Y</span>) to redo.
        </p>
      </div>
    ),
  },
  {
    id: "pages_and_add_page",
    title: "Switch pages and create new ones",
    targetId: TUTORIAL_TARGETS.NAVBAR_PAGE_CONTROLS,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>Use this control to move between pages in your website.</p>
        <p>
          Click the <span className="font-medium">+</span> button to create a new page, choose its layout, and set its navigation options.
        </p>
      </div>
    ),
  },
  {
    id: "device_preview",
    title: "Preview on different devices",
    targetId: TUTORIAL_TARGETS.NAVBAR_DEVICE_PREVIEW,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          Use device preview to see how your website looks on responsive, phone, tablet, laptop, and desktop sizes.
        </p>
        <p>For phone/tablet you can also switch orientation (portrait/landscape) and toggle the device frame.</p>
      </div>
    ),
  },
  {
    id: "navbar_tools",
    title: "Editor tools",
    targetId: TUTORIAL_TARGETS.NAVBAR_TOOLS,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>This group contains your main editor tools:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Animations</li>
          <li>Website defaults (styles for future creations)</li>
          <li>Page settings</li>
          <li>Navigation (navbar/sidebars/footer)</li>
          <li>Versions</li>
          <li>Light/Dark theme</li>
          <li>Feedback</li>
        </ul>
      </div>
    ),
  },
  {
    id: "publish",
    title: "Publish your changes",
    targetId: TUTORIAL_TARGETS.NAVBAR_PUBLISH,
    placement: "auto",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>Your edits are saved as you go, but they aren’t live until you publish.</p>
        <p>When you’re ready, click Publish to update your public website!</p>
      </div>
    ),
  },
  {
    id: "home_and_tutorial",
    title: "Dashboard & tutorial",
    targetId: TUTORIAL_TARGETS.NAVBAR_HOME_TUTORIAL,
    placement: "bottom",
    showNav: true,
    highlight: { paddingPx: 8, radiusPx: 12 },
    body: (
      <div className="space-y-2">
        <p>
          The <span className="font-medium">Home</span> button takes you back to your dashboard.
        </p>
        <p>
          The <span className="font-medium">Tutorial</span> button is always available so you can review this guide anytime.
        </p>
      </div>
    ),
  },
];

